import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Download, ChevronRight, Wrench, CheckCircle2, PauseCircle, PlayCircle, Package, CirclePlus } from "lucide-react";
import { useMaintenance } from "@/components/MaintenanceLayout";
import MachineIdentity from "@/components/maintenance/MachineMark";
import ReportBreakdownLightbox from "@/components/ReportBreakdownLightbox";
import { ReasonDialog, RepairedDialog } from "@/components/maintenance/MaintenanceDialogs";
import { exportBreakdowns } from "@/components/maintenance/exportBreakdowns";
import { RequestMachineDialog } from "@/components/maintenance/MachineRequestDialogs";
import {
  MAINT_STAGE_BY_KEY, humanDuration, humanDurationLive, agoFrom, dateLabel, formatINR,
  initialsOf, dayCountLabel, DOWN_ALERT_SECONDS,
  clockedSeconds, graceRemaining, liveCost,
} from "@/components/maintenance/lib";

// One stage page, driven by stage config — the four Maintenance stages are the
// same table card with different columns and one primary verb each. Breakdowns
// behave visually like PO line items moving across the ERP pipeline
// (MAINTENANCE_MODULE_PLAN.md §8c). There are no filter icons: the stage IS
// the filter.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COPY = {
  received: {
    sub: "New breakdowns land here — take one up to move it to Under Repair",
    empty: "No machines down right now — production reports land here.",
  },
  repair: {
    sub: "Who's on it, and how long the machine has been down",
    empty: "Nothing under repair. Take a breakdown up in Breakdowns Received and it moves here.",
  },
  handover: {
    sub: "Repaired machines wait here for production to confirm they run fine",
    empty: "When maintenance marks a machine repaired it waits here for production's sign-off.",
  },
  complete: {
    sub: "Closed breakdowns — the permanent record. Nothing here is ever deleted",
    empty: "Closed breakdowns collect here, with the total time down and what it cost.",
  },
};

// The money this breakdown has cost so far, ticking with the clock. Only ever
// shown where it is REAL: a stopped machine, with a ₹/hr rate set, past its
// grace period. Everything else stays quiet rather than guessing.
function MoneyTick({ ticket, nowMs }) {
  if (ticket.rate_inr_per_hour == null) return null;
  const cost = liveCost(ticket, nowMs);
  if (cost == null) return null;
  return (
    <div
      className="font-mono-data text-[11.5px] font-bold rl-tx-red mt-[3px]"
      title={`Accruing at ${formatINR(ticket.rate_inr_per_hour)}/hr while this machine is stopped`}
    >
      {formatINR(cost)}
    </div>
  );
}

// A stopped machine's clock is the module's one attention signal: it turns red
// once four CLOCKED hours have passed — the same line the alert email uses.
// Nothing counts during the grace period (10 minutes for a stopped machine,
// 3 hours for one that is still producing): the row shows the countdown to the
// clock starting instead, so the wait is visible rather than silent.
function DownFor({ ticket, nowMs }) {
  const waiting = graceRemaining(ticket, nowMs);
  const stopped = ticket.stoppage === "stopped";

  if (waiting > 0) {
    return (
      <>
        <div className="rl-dur text-[hsl(var(--muted-foreground))]" style={{ fontSize: 13 }}>
          {humanDurationLive(waiting)}
        </div>
        <div className="rl-dur-lbl" title={stopped
          ? "Maintenance has 10 minutes to take it up before the clock starts"
          : "The machine is still producing — the clock starts after 3 hours"}>
          {stopped ? "before the clock starts" : "still producing · grace"}
        </div>
      </>
    );
  }

  const secs = clockedSeconds(ticket, nowMs);
  const alert = stopped && secs >= DOWN_ALERT_SECONDS;
  const days = dayCountLabel(secs);
  return (
    <>
      {/* The seconds keep running — a machine that is down is losing money
          right now, and the row should feel that way. */}
      <div className={`rl-dur ${alert ? "rl-tx-red" : stopped ? "" : "rl-tx-amber"}`}>
        {humanDurationLive(secs)}
      </div>
      <div className="rl-dur-lbl">
        {stopped ? `machine down${days ? ` · ${days}` : ""}` : "running with the problem"}
      </div>
      <MoneyTick ticket={ticket} nowMs={nowMs} />
    </>
  );
}

function Problem({ ticket, note }) {
  return (
    <>
      <div className="text-[13.5px] font-[650] leading-[1.35]">{ticket.reason}</div>
      {note && <div className="text-[12px] text-[hsl(var(--muted-foreground))] mt-[3px] max-w-[400px] leading-[1.45]">{note}</div>}
    </>
  );
}

function Person({ name, sub }) {
  if (!name) return <span className="text-[hsl(var(--muted-foreground))] text-[12px]">—</span>;
  return (
    <>
      <div className="flex items-center gap-1.5 text-[11.5px]">
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-[#ef4444] to-[#f97316] text-white text-[8px] font-extrabold shrink-0">
          {initialsOf(name)}
        </span>
        <b className="font-[650]">{name}</b>
      </div>
      {sub && <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-[3px]">{sub}</div>}
    </>
  );
}

const Th = ({ children, right, width }) => (
  <th
    className={`px-4 py-2.5 text-[9.5px] uppercase tracking-[0.09em] font-semibold text-[hsl(var(--muted-foreground))] ${right ? "text-right" : "text-left"}`}
    style={width ? { width } : undefined}
  >
    {children}
  </th>
);
const Td = ({ children, right, className = "" }) => (
  <td className={`px-4 py-[15px] border-t border-[hsl(var(--border))]/60 align-middle ${right ? "text-right" : ""} ${className}`}>{children}</td>
);

// The Complete stage's expandable timeline — reported → taken up → pauses →
// repaired → sign-off, the History-Log pattern.
function Timeline({ ticket }) {
  const rows = [];
  rows.push(["Reported", ticket.reported_at, `${ticket.reported_by?.name || "—"} · ${ticket.reason}${ticket.note ? ` — ${ticket.note}` : ""}`]);
  if (ticket.taken_at) rows.push(["Taken up", ticket.taken_at, ticket.taken_by?.name || "—"]);
  for (const p of ticket.pauses || []) {
    rows.push(["Waiting for spares", p.from, `${p.by?.name || "—"} · ${p.reason || ""}`]);
    if (p.to) rows.push(["Repair resumed", p.to, p.by?.name || "—"]);
  }
  for (const s of ticket.sendbacks || []) {
    rows.push(["Sent back", s.at, `${s.by?.name || "—"} · ${s.reason || ""}`]);
  }
  if (ticket.repaired_at) rows.push(["Repaired", ticket.repaired_at, `${ticket.repaired_by?.name || "—"} · ${ticket.root_note || ""}${ticket.parts_note ? ` (parts: ${ticket.parts_note})` : ""}`]);
  if (ticket.closed_at) rows.push(["Confirmed working", ticket.closed_at, ticket.closed_by?.name || "—"]);
  const seg = ticket.segments || {};
  return (
    <div className="px-4 pb-4 pt-1">
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 p-3">
        <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 text-[11px] text-[hsl(var(--muted-foreground))]">
          <span>Response <b className="font-mono-data text-[hsl(var(--foreground))]">{seg.response != null ? humanDuration(seg.response) : "—"}</b></span>
          <span>Repair <b className="font-mono-data text-[hsl(var(--foreground))]">{seg.repair != null ? humanDuration(seg.repair) : "—"}</b></span>
          <span>Waiting for spares <b className="font-mono-data text-[hsl(var(--foreground))]">{humanDuration(seg.paused || 0)}</b></span>
          <span>Sign-off lag <b className="font-mono-data text-[hsl(var(--foreground))]">{seg.verification != null ? humanDuration(seg.verification) : "—"}</b></span>
        </div>
        <ol className="space-y-1.5">
          {rows.map(([label, at, detail], i) => (
            <li key={i} className="flex gap-3 text-[12px]">
              <span className="font-mono-data text-[10.5px] text-[hsl(var(--muted-foreground))] w-[110px] shrink-0 pt-[1px]">
                {at ? new Date(at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
              </span>
              <span className="font-[650] w-[140px] shrink-0">{label}</span>
              <span className="text-[hsl(var(--muted-foreground))] min-w-0">{detail}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export default function MaintenanceStagePage({ stage }) {
  const navigate = useNavigate();
  const ctx = useMaintenance() || {};
  const { can = {}, reasons = [], reasonOther = "Something else…", refresh, refreshKey } = ctx;
  const cfg = MAINT_STAGE_BY_KEY[stage];
  const copy = STAGE_COPY[stage];

  const [tickets, setTickets] = useState(null);   // null = still loading
  const [busyId, setBusyId] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showMachineReq, setShowMachineReq] = useState(false);
  const [dialog, setDialog] = useState(null);     // {kind, ticket}

  // The clocks are always running, so the page ticks even when nothing changes
  // — once a second, and only while the tab is actually on screen.
  useEffect(() => {
    const tick = () => { if (!document.hidden) setNowMs(Date.now()); };
    const t = setInterval(tick, 1000);
    const onVis = () => tick();
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/maintenance/tickets?stage=${stage}`);
      setTickets(r.data?.tickets || []);
    } catch (e) {
      setTickets([]);
      toast.error(e?.response?.data?.detail || "Could not load this stage.");
    }
  }, [stage]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // Every action goes through here: one call, one toast, one refresh of both
  // the rows and the nav counts.
  const act = useCallback(async (ticket, path, body, successMsg) => {
    setBusyId(ticket.id);
    try {
      await axios.post(`${API}/maintenance/tickets/${ticket.id}/${path}`, body || {});
      toast.success(successMsg);
      setDialog(null);
      await load();
      refresh?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "That didn't go through.");
    } finally {
      setBusyId(null);
    }
  }, [load, refresh]);

  const rows = useMemo(() => {
    if (!tickets) return null;
    if (stage !== "complete" || !search.trim()) return tickets;
    const q = search.trim().toLowerCase();
    return tickets.filter((t) =>
      (t.machine?.name || "").toLowerCase().includes(q) ||
      (t.machine_no || "").toLowerCase().includes(q) ||
      (t.reason || "").toLowerCase().includes(q) ||
      (t.root_note || "").toLowerCase().includes(q));
  }, [tickets, stage, search]);

  const headerRight = stage === "received" ? (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Anyone who can SEE the module can ask for a machine — spotting a new
          one on the floor isn't a privilege. An admin approves it. */}
      <button
        onClick={() => setShowMachineReq(true)}
        data-testid="mt-request-machine"
        title="A machine that isn't in RockLink yet? Ask an admin to add it"
        className="rl-pill flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold"
      >
        <CirclePlus className="w-3.5 h-3.5" /> Request a machine
      </button>
      {can.can_report_breakdown && (
        <button
          onClick={() => setShowReport(true)}
          data-testid="mt-report-breakdown"
          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full rl-lit-btn bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-semibold uppercase tracking-[0.05em] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Report Breakdown
        </button>
      )}
    </div>
  ) : stage === "complete" ? (
    <div className="flex items-center gap-2">
      <div className="rl-carved flex items-center gap-[7px] h-8 px-3.5 rounded-full bg-[hsl(var(--muted))]/50">
        <Search className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search machine or problem…"
          data-testid="mt-complete-search"
          className="bg-transparent outline-none text-[12px] w-[190px] placeholder:text-[hsl(var(--muted-foreground))]"
        />
      </div>
      <button
        onClick={() => {
          const r = exportBreakdowns(rows || []);
          toast.success(`Exported ${r.rowCount} breakdown${r.rowCount === 1 ? "" : "s"}`);
        }}
        data-testid="mt-export-breakdowns"
        className="rl-pill flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold"
      >
        <Download className="w-3.5 h-3.5" /> Export Excel
      </button>
    </div>
  ) : null;

  const colCount = stage === "complete" ? 6 : 5;

  return (
    <>
      <div className="rl-glass-edge relative rounded-[14px] overflow-hidden bg-[hsl(var(--card))]" data-testid={`mt-stage-${stage}`}>
        {/* 3px stage-accent top rule — swipes in on stage change. */}
        <div className="rl-stage-accent absolute top-0 left-0 right-0 h-[3px]" style={{ background: cfg.color, transformOrigin: "left" }} />
        <div className="px-[18px] pt-4 pb-3 flex items-center justify-between gap-3 flex-wrap border-b border-[hsl(var(--border))]">
          <div>
            <div className="text-[17px] font-extrabold flex items-center gap-2.5" style={{ fontFamily: "var(--rl-font-heading), sans-serif" }}>
              <span className="w-[9px] h-[9px] rounded-full" style={{ background: cfg.color }} />
              {cfg.label}
            </div>
            <div className="text-[11.5px] text-[hsl(var(--muted-foreground))] mt-[3px]">{copy.sub}</div>
          </div>
          {headerRight}
        </div>

        <table className="w-full border-collapse">
          <thead className="rl-thead">
            <tr>
              <Th width="30%">Machine</Th>
              {stage === "received" && (<><Th>Problem</Th><Th>Reported</Th><Th right>Down for</Th><Th right>Action</Th></>)}
              {stage === "repair" && (<><Th>Problem</Th><Th>Technician</Th><Th right>Down for</Th><Th right>Actions</Th></>)}
              {stage === "handover" && (<><Th>What was done</Th><Th>Repaired</Th><Th right>Total down</Th><Th right>Production confirms</Th></>)}
              {stage === "complete" && (<><Th>Problem</Th><Th>Repaired by</Th><Th>Closed</Th><Th right>Total downtime</Th><Th right>Cost</Th></>)}
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={colCount} className="px-4 py-10 text-center text-[12px] text-[hsl(var(--muted-foreground))]">Loading…</td></tr>
            )}
            {rows && rows.length === 0 && (
              <tr>
                <td colSpan={colCount} className="px-4 py-12 text-center">
                  <p className="text-[13px] text-[hsl(var(--muted-foreground))] max-w-[420px] mx-auto leading-relaxed">
                    {search.trim() ? "Nothing matches that search." : copy.empty}
                  </p>
                </td>
              </tr>
            )}
            {rows && rows.map((t) => {
              const busy = busyId === t.id;
              const isExpanded = expanded === t.id;
              return (
                <React.Fragment key={t.id}>
                  <tr
                    className={`hover:bg-[hsl(var(--muted))]/25 ${stage === "complete" ? "cursor-pointer" : ""}`}
                    onClick={stage === "complete" ? () => setExpanded(isExpanded ? null : t.id) : undefined}
                    data-testid={`mt-row-${t.id}`}
                  >
                    <Td><MachineIdentity machine={t.machine} showCategory={stage !== "complete"} /></Td>

                    {stage === "received" && (
                      <>
                        <Td><Problem ticket={t} note={t.note} /></Td>
                        <Td>
                          <div className="text-[11.5px]">by <b className="font-[650]">{t.reported_by?.name || "—"}</b>{t.reported_by_role ? ` · ${t.reported_by_role}` : ""}</div>
                          <div className="text-[10.5px] text-[hsl(var(--muted-foreground))]">{agoFrom(t.reported_at, nowMs)}</div>
                        </Td>
                        <Td right><DownFor ticket={t} nowMs={nowMs} /></Td>
                        <Td right>
                          {can.can_repair_machines ? (
                            <button
                              onClick={() => act(t, "take-up", null, `You're on ${t.machine?.name}`)}
                              disabled={busy}
                              data-testid={`mt-take-up-${t.id}`}
                              className="rl-lit-btn inline-flex items-center justify-center gap-1.5 h-8 px-3.5 rounded-full bg-[hsl(var(--primary))] text-white text-[12px] font-bold disabled:opacity-50"
                            >
                              <Wrench className="w-3.5 h-3.5" /> I'm taking this up
                            </button>
                          ) : (
                            <span
                              className="text-[11px] text-[hsl(var(--muted-foreground))] italic"
                              title="Only the maintenance team can take a breakdown up"
                            >
                              waiting for maintenance
                            </span>
                          )}
                          {/* Quiet withdrawal — only the reporter (or an admin),
                              and only while maintenance hasn't touched it. */}
                          {(can.can_admin_maintenance || t.reported_by?.user_id === ctx.userId) && (
                            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5">
                              <button
                                onClick={() => setDialog({ kind: "withdraw", ticket: t })}
                                data-testid={`mt-withdraw-${t.id}`}
                                className="underline underline-offset-2 hover:text-[hsl(var(--foreground))]"
                              >
                                Reported by mistake?
                              </button>
                            </div>
                          )}
                        </Td>
                      </>
                    )}

                    {stage === "repair" && (
                      <>
                        <Td>
                          <Problem ticket={t} note={t.open_pause ? `⏸ Waiting for ${t.open_pause.reason}` : t.note} />
                          {/* The linked spare order, while it is still open — a
                              true waiting alert, so amber. It clears when the
                              order is marked received; resuming stays manual. */}
                          {t.open_spare && (
                            <div className="text-[10.5px] rl-tx-amber mt-1 inline-flex items-center gap-1.5" title={`${t.open_spare.part_name} — ${t.open_spare.status}`}>
                              <Package className="w-3 h-3" />
                              <b className="font-mono-data font-bold">{t.open_spare.id}</b> · {t.open_spare.status}
                            </div>
                          )}
                          {t.sendback_count > 0 && (
                            <div className="text-[10.5px] rl-tx-amber mt-1">sent back {t.sendback_count}×</div>
                          )}
                        </Td>
                        <Td>
                          <Person
                            name={t.taken_by?.name}
                            sub={t.open_pause ? "paused — waiting for spares" : (t.taken_at ? `since ${new Date(t.taken_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : "")}
                          />
                        </Td>
                        <Td right><DownFor ticket={t} nowMs={nowMs} /></Td>
                        <Td right>
                          <div className="flex gap-[7px] justify-end flex-wrap">
                            {can.can_repair_machines ? (
                              <>
                                {t.open_pause ? (
                                  <button
                                    onClick={() => act(t, "resume", null, "Repair resumed")}
                                    disabled={busy}
                                    data-testid={`mt-resume-${t.id}`}
                                    className="rl-pill inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold disabled:opacity-50"
                                  >
                                    <PlayCircle className="w-3.5 h-3.5" /> Resume repair
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setDialog({ kind: "pause", ticket: t })}
                                    disabled={busy}
                                    data-testid={`mt-pause-${t.id}`}
                                    className="rl-pill inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold disabled:opacity-50"
                                  >
                                    <PauseCircle className="w-3.5 h-3.5" /> Waiting for spares
                                  </button>
                                )}
                                <button
                                  onClick={() => setDialog({ kind: "repaired", ticket: t })}
                                  disabled={busy || !!t.open_pause}
                                  title={t.open_pause ? "Resume the repair first" : undefined}
                                  data-testid={`mt-repaired-${t.id}`}
                                  className="rl-lit-btn inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-[12px] font-bold disabled:opacity-45 disabled:cursor-not-allowed"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Repaired — hand over
                                </button>
                              </>
                            ) : (
                              <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic" title="Only the maintenance team can work a repair">
                                maintenance is on it
                              </span>
                            )}
                          </div>
                        </Td>
                      </>
                    )}

                    {stage === "handover" && (
                      <>
                        <Td>
                          <div className="text-[13.5px] font-[650] leading-[1.35]">{t.reason}</div>
                          {t.root_note && <div className="text-[12px] text-[hsl(var(--muted-foreground))] mt-[3px] max-w-[400px] leading-[1.45]">“{t.root_note}”</div>}
                          {t.parts_note && <div className="text-[11px] text-[hsl(var(--muted-foreground))]/80 mt-1">parts: {t.parts_note}</div>}
                        </Td>
                        <Td>
                          <div className="text-[11.5px]">by <b className="font-[650]">{t.repaired_by?.name || "—"}</b></div>
                          <div className="text-[10.5px] text-[hsl(var(--muted-foreground))]">{agoFrom(t.repaired_at, nowMs)}</div>
                        </Td>
                        <Td right>
                          <div className={`rl-dur ${clockedSeconds(t, nowMs) >= DOWN_ALERT_SECONDS && t.stoppage === "stopped" ? "rl-tx-red" : ""}`}>
                            {humanDurationLive(clockedSeconds(t, nowMs))}
                          </div>
                          <div className="rl-dur-lbl">so far</div>
                          <MoneyTick ticket={t} nowMs={nowMs} />
                        </Td>
                        <Td right>
                          {can.can_verify_repairs ? (
                            <div className="flex gap-[7px] justify-end">
                              <button
                                onClick={() => act(t, "verify", null, `${t.machine?.name} signed back — breakdown closed`)}
                                disabled={busy}
                                data-testid={`mt-verify-${t.id}`}
                                className="rl-lit-btn inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-[12px] font-bold disabled:opacity-50"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" /> Working fine
                              </button>
                              <button
                                onClick={() => setDialog({ kind: "sendback", ticket: t })}
                                disabled={busy}
                                data-testid={`mt-sendback-${t.id}`}
                                className="rl-pill h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold disabled:opacity-50"
                              >
                                Still not right…
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-[7px] justify-end opacity-45 pointer-events-none" aria-hidden="true">
                              <span className="h-8 px-3.5 rounded-full bg-green-600 text-white text-[12px] font-bold inline-flex items-center gap-1.5">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Working fine
                              </span>
                              <span className="h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold inline-flex items-center">Still not right…</span>
                            </div>
                          )}
                          <div className="text-[10px] text-[hsl(var(--muted-foreground))] italic mt-1.5">
                            {can.can_verify_repairs
                              ? "Still-not-right asks what's wrong and returns it to Under Repair"
                              : "Production signs the machine back — this is their step"}
                          </div>
                        </Td>
                      </>
                    )}

                    {stage === "complete" && (
                      <>
                        <Td>
                          <div className="text-[12.5px] font-[550] text-[hsl(var(--foreground))]/80">{t.reason}</div>
                          {t.root_note && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-[3px] truncate max-w-[360px]">{t.root_note}</div>}
                        </Td>
                        <Td><span className="text-[11.5px] font-[650]">{t.repaired_by?.name || "—"}</span></Td>
                        <Td><span className="text-[10.5px] text-[hsl(var(--muted-foreground))]">{dateLabel(t.closed_at)}</span></Td>
                        <Td right><span className="rl-dur" style={{ fontSize: 13.5 }}>{humanDuration(t.down_seconds)}</span></Td>
                        <Td right>
                          <span
                            className={`font-mono-data text-[12.5px] font-bold ${t.cost_inr == null ? "text-[hsl(var(--muted-foreground))]" : ""}`}
                            title={t.cost_inr == null ? "No ₹/hr rate set for this machine yet" : undefined}
                          >
                            {t.cost_inr == null ? "—" : formatINR(t.cost_inr)}
                          </span>
                          <ChevronRight className={`inline w-3.5 h-3.5 ml-2 text-[hsl(var(--muted-foreground))]/60 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                        </Td>
                      </>
                    )}
                  </tr>
                  {stage === "complete" && isExpanded && (
                    <tr><td colSpan={colCount} className="bg-[hsl(var(--muted))]/10"><Timeline ticket={t} /></td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <RequestMachineDialog
        open={showMachineReq}
        onOpenChange={setShowMachineReq}
        onSent={() => refresh?.()}
      />

      <ReportBreakdownLightbox
        open={showReport}
        onOpenChange={setShowReport}
        onReported={async () => { await load(); refresh?.(); navigate("/maintenance/received"); }}
      />

      <ReasonDialog
        open={dialog?.kind === "pause"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Waiting for spares"
        description="The clock keeps running — this just records why the repair is paused."
        label="What are you waiting for?"
        placeholder="Seal kit from Doosan Chandigarh…"
        confirmLabel="Pause the repair"
        machine={dialog?.ticket?.machine}
        busy={busyId === dialog?.ticket?.id}
        withSpare
        onConfirm={(reason, spare) => act(dialog.ticket, "pause", { reason, spare },
          spare ? `Paused — ${spare.part_name} logged as ordered` : "Marked as waiting for spares")}
      />
      <ReasonDialog
        open={dialog?.kind === "sendback"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Still not right"
        description="This goes back to Under Repair with the same technician."
        label="What's still wrong?"
        placeholder="Same alarm came back after 20 minutes…"
        confirmLabel="Send back to repair"
        machine={dialog?.ticket?.machine}
        busy={busyId === dialog?.ticket?.id}
        onConfirm={(reason) => act(dialog.ticket, "send-back", { reason }, "Sent back to Under Repair")}
      />
      <ReasonDialog
        open={dialog?.kind === "withdraw"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Reported by mistake?"
        description="The report is archived with your reason — nothing is deleted."
        label="Why was this a mistake?"
        placeholder="Wrong machine — it was the one next to it…"
        confirmLabel="Withdraw the report"
        confirmTone="slate"
        machine={dialog?.ticket?.machine}
        busy={busyId === dialog?.ticket?.id}
        onConfirm={(reason) => act(dialog.ticket, "withdraw", { reason }, "Report withdrawn")}
      />
      <RepairedDialog
        open={dialog?.kind === "repaired"}
        onOpenChange={(v) => !v && setDialog(null)}
        ticket={dialog?.ticket}
        reasons={reasons}
        reasonOther={reasonOther}
        busy={busyId === dialog?.ticket?.id}
        onConfirm={(body) => act(dialog.ticket, "repaired", body, "Handed over — waiting for production's sign-off")}
      />
    </>
  );
}
